import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, ArrowRight } from 'lucide-react';
import new_user from '@/js/images/new-user-dashboard.svg';
import { Separator } from '@/components/ui/separator';
import FormContainer from './FormContainer';
import { useFormProgress } from '@/js/pages/Supplier/utils/filledFormshook';
import { registerSupplierAction } from '@/js/reduxstore/actions/authActions';
import createApiClient from '@/api/axiosInstance';
import NDAModal from '@/js/components/modals/NDAModal';
import { motion, AnimatePresence } from 'framer-motion';

// Utility: Transform API response to Redux format
const transformApiToRedux = (apiData, mapping) => {
  const transformedData = {};
  const flattenObject = (obj, parentKey = '') => {
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        const mappedKey = mapping[newKey] || key;

        if (
          typeof obj[key] === 'object' &&
          obj[key] !== null &&
          !Array.isArray(obj[key])
        ) {
          flattenObject(obj[key], mappedKey);
        } else {
          transformedData[mappedKey] = obj[key];
        }
      }
    }
  };
  flattenObject(apiData);
  return transformedData;
};

// Countdown Timer
const DateInterval = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const getTimeLeft = () => {
      const now = new Date();
      const target = targetDate;
      const difference = target - now;

      if (difference <= 0) return { days: 0, hours: 0, minutes: 0 };

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      return { days, hours, minutes };
    };

    const updateTime = () => {
      setIsUpdating(true);
      setTimeout(() => {
        setTimeLeft(getTimeLeft());
        setIsUpdating(false);
      }, 200);
    };

    setTimeLeft(getTimeLeft());
    const timer = setInterval(updateTime, 60000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const formatTimeDisplay = () => {
    const { days, hours, minutes } = timeLeft;

    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ${hours} hr ${minutes} min`;
    }

    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }

    if (minutes > 0) {
      return `${minutes} min`;
    }

    return 'Expired';
  };

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 24;
  const isExpired =
    timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0;

  return (
    <span
      className={`
        inline-flex items-center px-2 py-1 rounded-md font-medium
        transition-all duration-300 ease-in-out
        transform hover:scale-105 hover:cursor-default
        ${
          isExpired
            ? 'bg-red-100 text-red-800 animate-pulse'
            : isUrgent
              ? 'bg-orange-100 text-orange-800 animate-bounce'
              : 'bg-blue-100 text-blue-800'
        }
        ${isUpdating ? 'scale-110 brightness-110' : ''}
      `}
    >
      <div
        className={`transition-opacity duration-200 ${isUpdating ? 'opacity-50' : 'opacity-100'}`}
      >
        {formatTimeDisplay()}
      </div>
      {!isExpired && isUrgent && (
        <div className="ml-2 flex space-x-1">
          <div
            className={`w-1 h-1 rounded-full bg-current animate-pulse ${isUrgent ? 'animate-ping' : ''}`}
          ></div>
          <div
            className={`w-1 h-1 rounded-full bg-current animate-pulse delay-75 ${isUrgent ? 'animate-ping' : ''}`}
          ></div>
          <div
            className={`w-1 h-1 rounded-full bg-current animate-pulse delay-150 ${isUrgent ? 'animate-ping' : ''}`}
          ></div>
        </div>
      )}
    </span>
  );
};

// Quote Table
const Quote = ({ targetDate, quotationsData }) => {
  const navigate = useNavigate();
  const TableData = quotationsData.map((item) => ({
    companyName: item.company_name,
    Quantity: item.Quantity,
    QuoteNo: item.quote_number,
    Revision: item.Revision,
    targetDate: new Date(item.rfq_submission_deadline),
    Action: 'View',
  }));

  const handleActionClick = (action, quoteNo, index) => {
    if (action === 'View')
      navigate(`/supplier/rfq/${quoteNo}`, {
        state: { data: quotationsData[index] },
      });
    if (action === 'Sign NDA') setShowNDAModal(true);
  };

  const [showNDAModal, setShowNDAModal] = useState(false);

  return (
    <div>
      <h3 className="font-semibold text-md pl-2 text-[15px]">New Requests</h3>
      <div className="w-full p-4 pt-0 pl-0">
        <div className="w-full overflow-x-auto px-2">
          <table className="w-full text-sm text-left text-gray-700 border-separate [border-spacing:0_12px]">
            <thead className="bg-purple-100 text-gray-700 uppercase text-xs">
              <tr className="border border-black/60">
                <th className="px-4 py-3 rounded-l-sm">Company Name</th>
                <th className="px-4 py-3">Quantity</th>
                <th className="px-4 py-3">Quote No.</th>
                <th className="px-4 py-3">Revision</th>
                <th className="px-4 py-3">Submission Deadline</th>
                <th className="px-4 py-3 rounded-r-sm text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {TableData.map((item, index) => (
                <tr key={index} className="bg-white relative shadow-sm">
                  <td className="px-4 py-4 font-semibold rounded-l-sm">
                    {item.companyName}
                  </td>
                  <td className="px-4 py-4 text-center">{item.Quantity}</td>
                  <td className="px-4 py-4">{item.QuoteNo}</td>
                  <td className="px-4 py-4 text-center">{item.Revision}</td>
                  <td className="px-4 py-4 text-red-600 font-bold text-center">
                    <DateInterval targetDate={item.targetDate} />
                  </td>
                  <td className="px-4 py-4 rounded-r-sm text-right">
                    <button
                      onClick={() =>
                        handleActionClick(item.Action, item.QuoteNo, index)
                      }
                      className="bg-[#7636D9] hover:bg-[#6922d6] text-white text-xs px-3 py-1 rounded"
                    >
                      {item.Action}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <NDAModal
        open={showNDAModal}
        onClose={() => {
          setShowNDAModal(false);
        }}
      />
    </div>
  );
};

// Placeholder Before Quote
const BeforeQuote = () => (
  <div className="flex flex-row items-center justify-between">
    <div className="w-full p-1 pl-3 flex flex-col justify-center items-start relative">
      <h1 className="text-2xl font-semibold cursor-pointer text-gray-700 mb-4 pl-2">
        🎉 Earn <span className="text-lightPurple">$500</span> by Completing
        Your Profile!
      </h1>
      <div className="ml-4 text-gray-500">
        {[
          'Complete the form to showcase your expertise to potential buyers.',
          'Verified profiles are prioritized and highly trusted by buyers.',
          'Complete and verify your profile to earn a $500 reward as a token of appreciation for joining our platform and contributing value to buyers.',
        ].map((text, i) => (
          <h2 key={i} className="text-md mb-2 flex items-center cursor-pointer">
            <ChevronRight size={20} className="mr-1" />
            {text}
          </h2>
        ))}
      </div>
    </div>
    <div className="w-full md:w-2/5 flex justify-center items-center">
      <img src={new_user} alt="welcome" />
    </div>
  </div>
);

const RequestTable = ({ targetDate, showQuotes, quotationsData }) => (
  <div className="w-full relative min-h-[300px]">
    <AnimatePresence mode="wait">
      {showQuotes ? (
        <motion.div
          key="quote"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <Quote targetDate={targetDate} quotationsData={quotationsData} />
        </motion.div>
      ) : (
        <motion.div
          key="before-quote"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <BeforeQuote />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// Manufacturer Dashboard
const ManufacturerDashboard = () => {
  const [visibleForms, setVisibleForms] = useState(8);
  const [targetDate] = useState(new Date(Date.now() + 48 * 60 * 60 * 1000));
  const formsProgress = useFormProgress();
  const token = useSelector((state) => state.authReducer.token);
  const { name, user_type_id } = useSelector(
    (state) => state.authReducer?.userData || {},
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);
  const [quotationsData, setQuotationsData] = useState([]);

  useEffect(() => {
    const fetchProfileData = async () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try {
        const response = await apiClient.get('/api/manufacturer-profile/get');
        if (response.data.success) {
          const apiToReduxMapping = {
            contact_information: 'phoneNumber',
            major_customers: 'who_are_your_major_customers',
          };
          const transformedData = transformApiToRedux(
            response.data.data,
            apiToReduxMapping,
          );

          const documentResponse = await apiClient.get('/api/documents/get');
          if (documentResponse.data.success) {
            const groupedDocuments = {};
            documentResponse.data.data.forEach((doc) => {
              const base = doc.document_name.split('-')[0];
              groupedDocuments[base] = [
                ...(groupedDocuments[base] || []),
                doc.document_name,
              ];
            });
            Object.entries(groupedDocuments).forEach(([key, value]) => {
              groupedDocuments[key] = value.join(',');
            });

            const combinedData = {
              ...transformedData,
              ...groupedDocuments,
            };

            dispatch(registerSupplierAction(combinedData));
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    const fetchQuotationsData = async () => {
      try {
        const response = await apiClient.post(
          '/api/rfq/get',
          JSON.stringify({
            manufacturer_id: user_type_id,
          }),
        );

        const responseData = response.data;

        setQuotationsData(responseData.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchProfileData();
    fetchQuotationsData();
  }, []);

  const handleLoadMore = () => setVisibleForms(formsProgress.length);
  const handleShowLess = () => {
    setVisibleForms(6);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSectionChange = (sectionId) => {
    navigate('/supplier/register', { state: { sectionId } });
  };

  return (
    <div className="dashboard-container flex flex-col w-full font-roboto">
      <div className="space-y-4">
        <div className="p-2">
          <h1 className="font-semibold text-2xl md:text-3xl hover:text-darkerPurple ease-in duration-200">
            Hi, {name?.split(' ')[0]}
          </h1>
        </div>
        <RequestTable
          targetDate={targetDate}
          showQuotes={quotationsData.length > 0}
          quotationsData={quotationsData}
        />
      </div>

      <Separator className="my-6" />

      <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between gap-2">
          <h2 className="text-xl font-semibold text-gray-700">Forms</h2>
          <p className="text-gray-400 text-sm pr-5 flex items-center">
            It takes less than 5 minutes to complete each form!
          </p>
        </div>
        <p className="text-gray-500 text-sm">
          Completing these forms boosts your visibility to buyers and enhances
          your chances of receiving quotations.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pt-4">
          {formsProgress.slice(0, visibleForms).map((form, i) => (
            <FormContainer
              key={i}
              index={i}
              progress={form.progress}
              title={form.title}
              missingFields={form.missingFields}
              onSectionChange={handleSectionChange}
            />
          ))}
        </div>

        {/* Load More/Less Button */}
        {/* <div className="flex justify-center py-2">

          {visibleForms < formsProgress.length ? (
            <button
              onClick={handleLoadMore}
              className="px-4 py-2 bg-white border border-darkPurple text-lightPurple rounded-md hover:bg-darkPurple hover:text-white transition-colors flex items-center gap-2 group"
            >
              View all forms
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />
            </button>
          ) : (
            <button
              onClick={handleShowLess}
              className="px-4 py-2 bg-white border border-darkPurple text-lightPurple rounded-md hover:bg-darkPurple hover:text-white transition-colors"
            >
              Show Less
            </button>
          )}
        </div> */}
      </div>

      <Separator className="my-1" />
    </div>
  );
};

export default ManufacturerDashboard;
